import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { ClipboardList, Calendar, CheckCircle, Clock, Plus, Play, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
  KPIStrip,
  ConfirmDialog,
} from '@/components/common';
import { inventoryApi, CycleCount } from '@/api/services';
import { locationsApi, Location } from '@/api/services';
import { getErrorMessage } from '@/api/client';

type CycleCountStatus = CycleCount['status'];

const statusBadgeMap: Record<
  CycleCountStatus,
  'scheduled' | 'in_progress' | 'pending_approval' | 'approved' | 'cancelled'
> = {
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in_progress',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  CANCELLED: 'cancelled',
};

const countTypeLabels: Record<string, string> = {
  FULL: 'Full Count',
  ABC_A: 'ABC - A Items',
  ABC_B: 'ABC - B Items',
  ABC_C: 'ABC - C Items',
  SPOT: 'Spot Check',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function CycleCounts() {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<CycleCount[]>([]);
  const [filteredCounts, setFilteredCounts] = useState<CycleCount[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // Cancelling discards a count someone scheduled, and any progress against it,
  // so it asks first — as deactivating an item already does on the item list.
  const [cancelTarget, setCancelTarget] = useState<CycleCount | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createData, setCreateData] = useState({
    locationId: '',
    countType: 'FULL',
    scheduledDate: '',
  });
  const [creating, setCreating] = useState(false);

  // KPI metrics
  const totalCounts = counts.length;
  const scheduledCounts = counts.filter((c) => c.status === 'SCHEDULED').length;
  const inProgressCounts = counts.filter((c) => c.status === 'IN_PROGRESS').length;
  const pendingApprovalCounts = counts.filter((c) => c.status === 'PENDING_APPROVAL').length;

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = counts;
    if (statusFilter !== 'all') {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }
    if (locationFilter !== 'all') {
      filtered = filtered.filter((c) => c.locationId === locationFilter);
    }
    setFilteredCounts(filtered);
    // Selected ids survive a filter change but their rows do not, which would
    // leave the bar offering to approve counts that are no longer on screen.
    setSelectedIds([]);
  }, [statusFilter, locationFilter, counts]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [countsRes, locationsRes] = await Promise.all([
        inventoryApi.getCycleCounts(),
        locationsApi.getAll({ limit: 100 }),
      ]);
      setCounts(countsRes.data);
      setFilteredCounts(countsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createData.locationId || !createData.scheduledDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      await inventoryApi.createCycleCount({
        locationId: createData.locationId,
        countType: createData.countType,
        scheduledDate: createData.scheduledDate,
      });
      toast.success('Cycle count created successfully');
      setCreateOpen(false);
      setCreateData({ locationId: '', countType: 'FULL', scheduledDate: '' });
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCreating(false);
    }
  };

  const handleStart = async (countId: string) => {
    try {
      setActionLoading(countId);
      await inventoryApi.startCycleCount(countId);
      toast.success('Cycle count started');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (countId: string) => {
    try {
      setActionLoading(countId);
      await inventoryApi.approveCycleCount(countId);
      toast.success('Cycle count approved');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (countId: string) => {
    try {
      setActionLoading(countId);
      await inventoryApi.cancelCycleCount(countId);
      toast.success('Cycle count cancelled');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Approve several counts at once.
   *
   * There is no bulk endpoint, so this is N requests and each is reported on
   * its own. A partial result is the normal case — a count someone else moved
   * on in the meantime will fail while the rest succeed — so the toast says how
   * many of how many, rather than claiming success for the batch.
   */
  const handleBulkApprove = async () => {
    const approvable = counts.filter(
      (c) => selectedIds.includes(c.id) && c.status === 'PENDING_APPROVAL'
    );
    if (approvable.length === 0) return;

    setBulkApproving(true);
    const failures: string[] = [];
    for (const count of approvable) {
      try {
        await inventoryApi.approveCycleCount(count.id);
      } catch {
        failures.push(count.countNumber);
      }
    }
    setBulkApproving(false);

    const done = approvable.length - failures.length;
    if (done > 0) toast.success(`Approved ${done} of ${approvable.length}`);
    if (failures.length > 0) toast.error(`Could not approve ${failures.join(', ')}`);

    setSelectedIds([]);
    await loadData();
  };

  if (loading) {
    return <LoadingState />;
  }

  // Only counts awaiting approval can be approved, and a selection may mix
  // states — the button says how many it will actually act on.
  const pendingSelected = counts.filter(
    (c) => selectedIds.includes(c.id) && c.status === 'PENDING_APPROVAL'
  ).length;

  const columns: Column<CycleCount>[] = [
    {
      key: 'countNumber',
      header: 'Count #',
      sortable: true,
      render: (count) => <span className="truncate font-semibold">{count.countNumber}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      render: (count) => (
        <div>
          <div className="font-medium">{count.location?.name || '-'}</div>
          <div className="text-xs leading-4 text-muted-foreground">{count.location?.code || ''}</div>
        </div>
      ),
    },
    {
      key: 'countType',
      header: 'Type',
      render: (count) => (
        <Badge variant="secondary">{countTypeLabels[count.countType] || count.countType}</Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (count) => <StatusBadge status={statusBadgeMap[count.status]} />,
    },
    {
      key: 'items',
      header: 'Items Count',
      render: (count) => <span className="text-muted-foreground">{count._count?.items ?? 0}</span>,
    },
    {
      key: 'scheduledDate',
      header: 'Scheduled Date',
      sortable: true,
      render: (count) => (
        <span className="text-muted-foreground">{formatDate(count.scheduledDate)}</span>
      ),
    },
    {
      key: 'countedBy',
      header: 'Counted By',
      render: (count) => (
        <span className="text-muted-foreground">{count.countedBy?.fullName || '-'}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-40',
      render: (count) => {
        const isLoading = actionLoading === count.id;
        return (
          <div className="flex items-center gap-1">
            {count.status === 'SCHEDULED' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStart(count.id);
                  }}
                  disabled={isLoading}
                >
                  <Play className="mr-1 h-3 w-3" />
                  Start
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCancelTarget(count);
                  }}
                  disabled={isLoading}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="mr-1 h-3 w-3" />
                  Cancel
                </Button>
              </>
            )}
            {count.status === 'IN_PROGRESS' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setCancelTarget(count);
                }}
                disabled={isLoading}
                className="text-destructive hover:text-destructive"
              >
                <X className="mr-1 h-3 w-3" />
                Cancel
              </Button>
            )}
            {count.status === 'PENDING_APPROVAL' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(count.id);
                  }}
                  disabled={isLoading}
                >
                  <Check className="mr-1 h-3 w-3" />
                  Approve
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCancelTarget(count);
                  }}
                  disabled={isLoading}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="mr-1 h-3 w-3" />
                  Cancel
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Inventory', href: '/inventory' },
            { label: 'Cycle Counts' },
          ]}
          title="Cycle Counts"
          description="Scheduled stock checks by location, with type, item coverage, and the counter assigned."
          actions={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Count
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIStrip columns={4}>
          <MetricCard
            label="Total Counts"
            value={totalCounts}
            sentiment="info"
            icon={<ClipboardList className="h-5 w-5" />}
          />
          <MetricCard
            label="Scheduled"
            value={scheduledCounts}
            sentiment="neutral"
            icon={<Calendar className="h-5 w-5" />}
          />
          <MetricCard
            label="In Progress"
            value={inProgressCounts}
            sentiment="warning"
            icon={<Clock className="h-5 w-5" />}
          />
          <MetricCard
            label="Pending Approval"
            value={pendingApprovalCounts}
            sentiment="warning"
            icon={<CheckCircle className="h-5 w-5" />}
          />
        </KPIStrip>
      </div>

      {/* Filters */}
      {/* Data Table */}
      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          data={filteredCounts}
          columns={columns}
          toolbar={
            <>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          }
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          getRowId={(count) => count.id}
          emptyTitle="No cycle counts found"
          emptyDescription="Get started by scheduling your first cycle count."
          emptyAction={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Count
            </Button>
          }
          pagination
          exportable
          columnToggle
        />
      </motion.div>

      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border bg-card p-3 shadow-floating">
          <span className="pl-1 text-sm font-medium">
            {selectedIds.length} count{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button
            size="sm"
            onClick={handleBulkApprove}
            disabled={bulkApproving || pendingSelected === 0}
          >
            <Check className="mr-2 h-4 w-4" />
            {pendingSelected === 0 ? 'None awaiting approval' : `Approve ${pendingSelected}`}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title="Cancel this count?"
        description={
          cancelTarget?.status === 'IN_PROGRESS'
            ? `${cancelTarget?.countNumber} at ${cancelTarget?.location?.name ?? 'this location'} is part-counted. Cancelling discards the quantities recorded so far.`
            : `${cancelTarget?.countNumber} at ${cancelTarget?.location?.name ?? 'this location'} will be cancelled. Scheduling it again means creating a new count.`
        }
        confirmLabel="Cancel count"
        cancelLabel="Keep it"
        variant="destructive"
        loading={!!cancelTarget && actionLoading === cancelTarget.id}
        onConfirm={() => {
          if (cancelTarget) handleCancel(cancelTarget.id);
          setCancelTarget(null);
        }}
      />

      {/* Create Cycle Count Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Cycle Count</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Location *</label>
              <Select
                value={createData.locationId}
                onValueChange={(value) => setCreateData({ ...createData, locationId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Count Type *</label>
              <Select
                value={createData.countType}
                onValueChange={(value) => setCreateData({ ...createData, countType: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select count type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FULL">Full Count</SelectItem>
                  <SelectItem value="ABC_A">ABC - A Items</SelectItem>
                  <SelectItem value="ABC_B">ABC - B Items</SelectItem>
                  <SelectItem value="ABC_C">ABC - C Items</SelectItem>
                  <SelectItem value="SPOT">Spot Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Scheduled Date *</label>
              <Input
                type="date"
                value={createData.scheduledDate}
                onChange={(e) => setCreateData({ ...createData, scheduledDate: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating...' : 'Schedule Count'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
